import React, { useMemo, useState } from 'react'
import moment from 'moment-timezone'
import {
  ArrowsDownUpIcon,
  PencilSimpleIcon,
  TreeStructureIcon,
} from '@phosphor-icons/react'
import { Tribe } from '../../types/Tribe'

type SortColumn = 'name' | 'created_at'

const activeTeams = (tribe: Tribe) =>
  tribe.teams.filter(
    team => !team.endDate || new Date(team.endDate).valueOf() > Date.now(),
  )

interface Props {
  tribes: Tribe[]
  onRowClick: (tribe: Tribe) => void
  onEdit: (tribe: Tribe) => void
}

const TribesTable = ({ tribes, onRowClick, onEdit }: Props) => {
  const [sortColumn, setSortColumn] = useState<SortColumn>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const sortedTribes = useMemo(() => {
    return [...tribes].sort((a, b) => {
      const comparison =
        sortColumn === 'created_at'
          ? moment(a.created_at).valueOf() - moment(b.created_at).valueOf()
          : a.name.localeCompare(b.name)
      return sortOrder === 'asc' ? comparison : -comparison
    })
  }, [tribes, sortColumn, sortOrder])

  const handleSort = (column: SortColumn) => {
    if (column === sortColumn) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const sortableHeader = (label: string, column: SortColumn, width: string) => (
    <th className="manage-tribes__table-th" style={{ width }}>
      <button
        type="button"
        className="manage-tribes__sort-btn"
        onClick={() => handleSort(column)}
      >
        {label}
        <ArrowsDownUpIcon size={14} weight="regular" color="#717182" />
      </button>
    </th>
  )

  if (tribes.length === 0) {
    return (
      <div className="manage-tribes__empty">
        <div className="manage-tribes__empty-icon">
          <TreeStructureIcon size={48} weight="regular" color="#717182" />
        </div>
        <p className="manage-tribes__empty-label">
          No tribes found. Try adjusting your search.
        </p>
      </div>
    )
  }

  return (
    <div className="manage-tribes__table-card">
      <table className="manage-tribes__table">
        <thead className="manage-tribes__table-head">
          <tr>
            {sortableHeader('Tribe', 'name', '20%')}
            {sortableHeader('Created On', 'created_at', '18%')}
            <th className="manage-tribes__table-th" style={{ width: '50%' }}>
              Teams
            </th>
            <th className="manage-tribes__table-th" style={{ width: '12%' }}>
              Members
            </th>
            <th className="manage-tribes__table-th manage-tribes__table-th--actions" />
          </tr>
        </thead>
        <tbody>
          {sortedTribes.map(tribe => (
            <tr
              key={tribe.id}
              className="manage-tribes__table-row"
              onClick={() => onRowClick(tribe)}
            >
              <td className="manage-tribes__table-td">{tribe.name}</td>
              <td className="manage-tribes__table-td">
                {moment(tribe.created_at).format('MM/DD/YYYY')}
              </td>
              <td className="manage-tribes__table-td manage-tribes__table-td--teams">
                <div className="manage-tribes__teams-list">
                  {activeTeams(tribe).map(team => (
                    <span key={team.id} className="manage-tribes__team-pill">
                      {team.name}
                    </span>
                  ))}
                </div>
              </td>
              <td className="manage-tribes__table-td">
                {tribe.activeMembersCount ?? 0}
              </td>
              <td className="manage-tribes__table-td manage-tribes__table-td--actions">
                <button
                  type="button"
                  className="manage-tribes__edit-btn"
                  aria-label={`Edit ${tribe.name}`}
                  title="Edit"
                  onClick={e => {
                    e.stopPropagation()
                    onEdit(tribe)
                  }}
                >
                  <PencilSimpleIcon size={18} weight="regular" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default TribesTable
